/**
 * Base Axios HTTP Client
 *
 * This is the foundation of all API communication in the Vue app.
 * It provides a centralized, configured Axios instance that all other
 * services will use.
 */

import axios from 'axios'

// Create the base Axios instance
const api = axios.create({
  // Base URL: empty string means relative URLs (same origin)
  // In production, Vue and API are served from same origin, so relative URLs work
  // In development with Vite, the proxy handles /api calls
  // Only use localhost fallback if VITE_API_URL is explicitly undefined
  baseURL: import.meta.env.VITE_API_URL !== undefined ? import.meta.env.VITE_API_URL : 'http://localhost:8003',

  // CRITICAL: Include session cookies in every request
  // FastAPI uses session cookies for authentication
  // Without this, the backend won't recognize logged-in users
  withCredentials: true,

  // Default headers
  headers: {
    'Content-Type': 'application/json'
  },

  // Timeout after 30 seconds
  timeout: 30000
})

// Request interceptor
// This runs BEFORE every API call
api.interceptors.request.use(
  config => {
    // Log all API requests in development (helpful for debugging)
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, config.data || '')
    }
    return config
  },
  error => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor
// This runs AFTER every API call
api.interceptors.response.use(
  response => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method.toUpperCase()} ${response.config.url}`, response.data)
    }
    return response
  },
  error => {
    // Log errors in development
    if (import.meta.env.DEV) {
      console.error('[API Response Error]', error.response?.data || error.message)
    }

    // Handle specific HTTP status codes
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Unauthorized - session expired or not logged in
          console.warn('Session expired or unauthorized')
          // Don't auto-redirect here - let components handle it
          // (Router guards will handle redirects)
          break

        case 403:
          // Forbidden - user doesn't have permission
          console.warn('Access forbidden')
          break

        case 404:
          // Not found
          console.warn('Resource not found')
          break

        case 500:
          // Server error
          console.error('Server error occurred')
          break
      }
    } else if (error.request) {
      // Request was made but no response received (network error)
      console.error('Network error - no response from server')
    } else {
      // Something else happened
      console.error('Request error:', error.message)
    }

    return Promise.reject(error)
  }
)

export default api
